import { useState, useRef } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { 
  X, Loader2, Sparkles, Lock, ArrowRight, 
  BookOpen, RotateCcw, Save, FileDown, Check
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTED_TOPICS = [
  'Forgiveness',
  'The nature of God',
  'Life after death',
  'Prayer and meditation',
  'Salvation and liberation',
  'The problem of suffering',
  'Love and compassion',
  'Sin and moral failure',
  'The role of faith',
  'Creation and the universe',
  'Marriage and family',
  'Charity and giving',
];

const RELIGIONS = [
  { id: 'Islam', label: 'Islam' },
  { id: 'Judaism', label: 'Judaism' },
  { id: 'Buddhism', label: 'Buddhism' },
  { id: 'Hinduism', label: 'Hinduism' },
  { id: 'Sikhism', label: 'Sikhism' },
  { id: 'Taoism', label: 'Taoism' },
];

export default function ReligionCompare({ onClose, effectiveTier, userRole }) {
  const [thought, setThought] = useState('');
  const [selectedReligions, setSelectedReligions] = useState(RELIGIONS.map(r => r.id));
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('cards');
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const resultRef = useRef(null);

  const isPro = effectiveTier === 'pro' || effectiveTier === 'church' || userRole === 'platform_admin' || userRole === 'admin';

  const toggleReligion = (id) => {
    setSelectedReligions(prev => 
      prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
    );
  };

  const handleCompare = async () => {
    if (!thought.trim() || selectedReligions.length === 0) return;
    setLoading(true);
    setError('');
    setResult('');
    setProgress('Analyzing perspectives...');

    try {
      const res = await axios.post(`${API}/generate/compare-religions`, {
        thought: thought.trim(),
        religions: selectedReligions,
      });

      if (res.data.job_id) {
        const jobId = res.data.job_id;
        let attempts = 0;
        while (attempts < 60) {
          await new Promise(r => setTimeout(r, 3000));
          attempts++;
          try {
            const status = await axios.get(`${API}/generate/status/${jobId}`);
            if (status.data.progress) setProgress(status.data.progress);
            if (status.data.status === 'completed') {
              setResult(status.data.content || '');
              break;
            } else if (status.data.status === 'failed') {
              setError(status.data.error || 'Comparison failed.');
              break;
            }
          } catch { /* continue polling */ }
        }
      }
    } catch (err) {
      if (err.response?.status === 403) {
        setError('This is a Pro feature. Please upgrade your plan.');
      } else {
        setError(err.response?.data?.detail || 'Something went wrong.');
      }
    } finally {
      setLoading(false);
      setProgress('');
    }
  };

  const handleReset = () => {
    setResult('');
    setThought('');
    setError('');
    setSaved(false);
  };

  const handleSave = async () => {
    if (!result) return;
    setIsSaving(true);
    try {
      await axios.post(`${API}/content/save`, {
        title: `Compare: ${thought}`,
        content_type: 'compare-religions',
        content: result,
        form_data: { thought, religions: selectedReligions }
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('Failed to save comparison');
    } finally {
      setIsSaving(false);
    }
  };

  const handleExportPDF = async () => {
    if (!resultRef.current) return;
    setIsExportingPDF(true);
    try {
      const canvas = await html2canvas(resultRef.current, {
        scale: 2,
        useCORS: true,
        logging: false
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;

      // Title
      pdf.setFontSize(14);
      pdf.setTextColor(27, 58, 107);
      pdf.text(`Comparative Study: ${thought}`, pdfWidth / 2, 20, { align: 'center' });
      pdf.setFontSize(9);
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Religions: ${selectedReligions.join(', ')}`, pdfWidth / 2, 28, { align: 'center' });

      const pageHeight = pdfHeight - 40;
      const scaledHeight = imgHeight * ratio;
      let heightLeft = scaledHeight;
      let position = 35;

      pdf.addImage(imgData, 'PNG', imgX, position, imgWidth * ratio, imgHeight * ratio);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - scaledHeight + 10;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', imgX, position, imgWidth * ratio, imgHeight * ratio);
        heightLeft -= pageHeight;
      }

      pdf.save(`compare-${thought.replace(/\s+/g, '-').toLowerCase().slice(0, 40)}.pdf`);
    } catch (err) {
      console.error('PDF export error:', err);
      setError('Failed to export PDF');
    } finally {
      setIsExportingPDF(false);
    }
  };

  // Parse the markdown to extract religion sections for card view
  const parseCards = (markdown) => {
    const sections = [];
    const parts = markdown.split(/^## /gm).filter(Boolean);
    for (const part of parts) {
      const lines = part.trim().split('\n');
      const title = lines[0].trim();
      const body = lines.slice(1).join('\n').trim();
      if (title && body && !title.includes('COMPARATIVE SUMMARY') && !title.includes('REFLECTION')) {
        sections.push({ title, body });
      }
    }
    return sections;
  };

  // Extract the table section
  const extractTable = (markdown) => {
    const tableMatch = markdown.match(/## COMPARATIVE SUMMARY TABLE[\s\S]*?(\|[\s\S]*?\|)\s*(?=##|$)/);
    return tableMatch ? tableMatch[0] : '';
  };

  const extractReflection = (markdown) => {
    const match = markdown.match(/## REFLECTION[\s\S]*$/);
    return match ? match[0] : '';
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto p-4 md:p-8" data-testid="religion-compare-modal">
      <div className="bg-[#F9F8F6] rounded-2xl w-full max-w-6xl shadow-2xl my-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E2DFD8]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#6B5B4B] flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-[#D4B896]" />
            </div>
            <div>
              <h2 className="font-heading text-lg font-bold text-[#0F1A2A]" data-testid="compare-title">Comparative Religion Study</h2>
              <p className="text-xs text-[#475569]">Explore perspectives across world religions</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#E2DFD8] rounded-lg transition-colors" data-testid="compare-close-btn">
            <X className="w-5 h-5 text-[#475569]" />
          </button>
        </div>

        {!isPro ? (
          /* Pro Gate */
          <div className="p-12 text-center" data-testid="compare-pro-gate">
            <Lock className="w-12 h-12 text-[#B8860B] mx-auto mb-4" />
            <h3 className="text-xl font-bold text-[#0F1A2A] mb-2">Pro Feature</h3>
            <p className="text-[#475569] mb-6 max-w-md mx-auto">
              Comparative religion analysis is available on Pro and Church plans. Upgrade to explore how your faith connects with world perspectives.
            </p>
            <a href="/pricing" className="inline-flex items-center gap-2 px-6 py-3 bg-[#1B3A6B] text-white rounded-xl font-medium hover:bg-[#254B8A] transition-colors" data-testid="compare-upgrade-btn">
              Upgrade to Pro <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        ) : !result ? (
          /* Input Form */
          <div className="p-6 space-y-6">
            {/* Thought input */}
            <div>
              <label className="block text-sm font-medium text-[#0F1A2A] mb-2">Your thought or concept</label>
              <textarea
                value={thought}
                onChange={(e) => setThought(e.target.value)}
                placeholder="E.g., What does forgiveness look like across world religions?"
                className="w-full px-4 py-3 rounded-xl border border-[#E2DFD8] bg-white text-[#0F1A2A] placeholder:text-[#94A3B8] focus:ring-2 focus:ring-[#1B3A6B]/30 focus:border-[#1B3A6B] outline-none resize-none"
                rows={3}
                data-testid="compare-thought-input"
              />
            </div>

            {/* Suggested topics */}
            <div>
              <label className="block text-sm font-medium text-[#0F1A2A] mb-2">Or choose a topic</label>
              <div className="flex flex-wrap gap-2">
                {SUGGESTED_TOPICS.map(topic => (
                  <button
                    key={topic}
                    onClick={() => setThought(topic)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                      thought === topic 
                        ? 'bg-[#1B3A6B] text-white border-[#1B3A6B]' 
                        : 'bg-white text-[#475569] border-[#E2DFD8] hover:border-[#1B3A6B] hover:text-[#1B3A6B]'
                    }`}
                    data-testid={`compare-topic-${topic.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {topic}
                  </button>
                ))}
              </div>
            </div>

            {/* Religion selection */}
            <div>
              <label className="block text-sm font-medium text-[#0F1A2A] mb-2">Compare with</label>
              <div className="flex flex-wrap gap-2">
                {RELIGIONS.map(r => (
                  <button
                    key={r.id}
                    onClick={() => toggleReligion(r.id)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      selectedReligions.includes(r.id) 
                        ? 'bg-[#6B5B4B] text-white border-[#6B5B4B]' 
                        : 'bg-white text-[#475569] border-[#E2DFD8] hover:border-[#6B5B4B]'
                    }`}
                    data-testid={`compare-religion-${r.id.toLowerCase()}`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>

            {error && (
              <div className="bg-red-50 text-red-700 px-4 py-3 rounded-xl text-sm">{error}</div>
            )}

            {/* Generate button */}
            <button
              onClick={handleCompare}
              disabled={loading || !thought.trim() || selectedReligions.length === 0}
              className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-[#1B3A6B] text-white rounded-xl font-medium hover:bg-[#254B8A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="compare-generate-btn"
            >
              {loading ? (
                <><Loader2 className="w-5 h-5 animate-spin" />{progress || 'Analyzing...'}</>
              ) : (
                <><Sparkles className="w-5 h-5" />Compare Perspectives</>
              )}
            </button>
          </div>
        ) : (
          /* Results */
          <div className="p-6 space-y-4">
            {/* View toggle + actions */}
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex gap-2">
                <button
                  onClick={() => setViewMode('cards')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${viewMode === 'cards' ? 'bg-[#1B3A6B] text-white' : 'bg-white text-[#475569] border border-[#E2DFD8]'}`}
                  data-testid="compare-view-cards"
                >
                  Cards
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${viewMode === 'table' ? 'bg-[#1B3A6B] text-white' : 'bg-white text-[#475569] border border-[#E2DFD8]'}`}
                  data-testid="compare-view-table"
                >
                  Table
                </button>
                <button
                  onClick={() => setViewMode('full')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${viewMode === 'full' ? 'bg-[#1B3A6B] text-white' : 'bg-white text-[#475569] border border-[#E2DFD8]'}`}
                  data-testid="compare-view-full"
                >
                  Full
                </button>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSave}
                  disabled={isSaving || saved}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    saved 
                      ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' 
                      : 'bg-white text-[#475569] border border-[#E2DFD8] hover:border-[#1B3A6B] hover:text-[#1B3A6B]'
                  } disabled:opacity-50`}
                  data-testid="compare-save-btn"
                >
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                  {saved ? 'Saved' : 'Save'}
                </button>
                <button
                  onClick={handleExportPDF}
                  disabled={isExportingPDF}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-white text-[#475569] border border-[#E2DFD8] hover:border-[#B8860B] hover:text-[#B8860B] transition-colors disabled:opacity-50"
                  data-testid="compare-export-pdf-btn"
                >
                  {isExportingPDF ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                  {isExportingPDF ? 'Exporting...' : 'Export PDF'}
                </button>
                <button onClick={handleReset} className="flex items-center gap-2 px-4 py-2 text-sm text-[#475569] hover:text-[#1B3A6B] transition-colors" data-testid="compare-reset-btn">
                  <RotateCcw className="w-4 h-4" /> New
                </button>
              </div>
            </div>

            <div className="text-sm text-[#475569] mb-2">
              Topic: <span className="font-medium text-[#0F1A2A]">"{thought}"</span>
            </div>

            <div ref={resultRef}>
            {viewMode === 'cards' && (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="compare-cards-view">
                {parseCards(result).map((card, i) => (
                  <div
                    key={i}
                    className={`rounded-xl border p-5 ${
                      card.title.includes('CHRISTIAN') 
                        ? 'bg-[#1B3A6B]/5 border-[#1B3A6B]/20 col-span-1 md:col-span-2 xl:col-span-3' 
                        : 'bg-white border-[#E2DFD8]'
                    }`}
                    data-testid={`compare-card-${i}`}
                  >
                    <h3 className={`font-bold text-base mb-3 ${card.title.includes('CHRISTIAN') ? 'text-[#1B3A6B]' : 'text-[#0F1A2A]'}`}>
                      {card.title}
                    </h3>
                    <div className="prose prose-sm max-w-none text-[#334155]">
                      <ReactMarkdown>{card.body}</ReactMarkdown>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {viewMode === 'table' && (
              <div className="space-y-6" data-testid="compare-table-view">
                <div className="overflow-x-auto bg-white rounded-xl border border-[#E2DFD8]">
                  <div className="prose prose-sm max-w-none p-4">
                    <ReactMarkdown>{extractTable(result)}</ReactMarkdown>
                  </div>
                </div>
                <div className="bg-[#1B3A6B]/5 rounded-xl border border-[#1B3A6B]/20 p-5">
                  <div className="prose prose-sm max-w-none text-[#334155]">
                    <ReactMarkdown>{extractReflection(result)}</ReactMarkdown>
                  </div>
                </div>
              </div>
            )}

            {viewMode === 'full' && (
              <div className="bg-white rounded-xl border border-[#E2DFD8] p-6" data-testid="compare-full-view">
                <div className="prose prose-sm max-w-none text-[#334155]">
                  <ReactMarkdown>{result}</ReactMarkdown>
                </div>
              </div>
            )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
